import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Zoom from "react-medium-image-zoom";

import { ReactComponent as LeftArrow } from "../../resources/icons/left_arrow.svg";
import downArrow from "./../../resources/down_arrow.png";
import DocumentShow from "../elements/DocumentShow";
import SubtaskList from "../elements/SubtaskList";
import ProjectDataSection from "../elements/ProjectDataSection";
import ProjectParticipants from "../elements/participants/ProjectParticipants";
import { getDocumentPresignedUrl, getProjectById } from "../../api/projects";
import PDFPreviewModal from "../elements/modals/PDFPreviewModal";

const documentTypes = [
  { title: "Building Permit", key: "PERMIT" },
  { title: "AKT 14", key: "AKT14" },
  { title: "AKT 15", key: "AKT15" },
  { title: "AKT 16", key: "AKT16" },
  { title: "Other Documents", key: "OTHER" },
];

const getStatusStyle = (status: string) => {
  switch (status.toLowerCase()) {
    case "completed":
      return "text-[#067647] bg-[#ECFDF3] border-[#ABEFC6]";
    case "cancelled":
      return "text-[#B42318] bg-[#FEF3F2] border-[#FECDCA]";
    default:
      return "text-[#344054] bg-[#F9FAFB] border-[#D0D5DD]";
  }
};

const FinishedProjectPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<any>(null);
  const [documents, setDocuments] = useState<Record<string, any[]>>({});
  const [openDocs, setOpenDocs] = useState(true);

  const [previewUrl, setPreviewUrl] = useState("");
  const [previewName, setPreviewName] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handlePreview = async (docId: string | number, name: string) => {
    const { data, error } = await getDocumentPresignedUrl(docId);
    if (error || !data?.file?.url) {
      alert("Error loading document preview.");
      return;
    }
    setPreviewUrl(data.file.url);
    setPreviewName(decodeURIComponent(name));
    setIsPreviewOpen(true);
  };

  const fetchProject = useCallback(async () => {
    if (projectId) {
      const { data, error } = await getProjectById(parseInt(projectId));

      if (error) {
        navigate("/projects");
        return;
      }

      if (data) {
        setProject(data);

        const grouped = (data.documents || []).reduce((acc: any, doc: any) => {
          acc[doc.type] = acc[doc.type] || [];
          acc[doc.type].push(doc);
          return acc;
        }, {} as Record<string, any[]>);

        setDocuments(grouped);
      }
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  if (!project) return <div>Loading…</div>;

  return (
    <div className="flex">
      <div className="flex-1 p-6 ml-[17.5rem] flex flex-col">
        <div className="flex items-center mb-4 border-b pb-4">
          <button onClick={() => navigate("/projects")} className="mr-3">
            <LeftArrow className="w-6 h-6" style={{ color: "#344054" }} />
          </button>
          <h1 className="text-2xl font-bold text-wrap">{project.title}</h1>
          <span
            className={`ml-4 text-sm font-medium px-3 py-1 border rounded-full ${getStatusStyle(
              project.status
            )}`}
          >
            {project.status[0].toUpperCase() +
              project.status.slice(1).toLowerCase()}
          </span>
        </div>

        <div className="flex justify-between gap-6 mb-6">
          <div className="flex flex-1">
            <div>
              <h2 className="text-lg font-semibold mb-1">
                Project details summary
              </h2>
              <p className="max-w-[500px] max-h-[100px] overflow-auto pr-2 whitespace-pre-wrap break-words text-[#475467]">
                {project.summary}
              </p>

              <ProjectParticipants
                supervisor={project.supervisor}
                contractor={project.contractor}
                assignor={project.assignor}
              />
            </div>
          </div>

          <ProjectDataSection project={project} />
        </div>

        <div
          className="flex items-center cursor-pointer mb-4"
          onClick={() => setOpenDocs(!openDocs)}
        >
          <h2 className="font-semibold">Documents</h2>
          <img
            src={downArrow}
            alt="Toggle"
            className={`w-5 h-5 ml-2 transform ${openDocs ? "" : "rotate-180"}`}
          />
        </div>
        {openDocs &&
          documentTypes.map(({ title, key }) => (
            <DocumentShow
              key={key}
              title={title}
              docs={documents[key] || []}
              onPreview={(docId, name) => handlePreview(docId, name)}
            />
          ))}

        <PDFPreviewModal
          url={previewUrl}
          name={previewName}
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
        />

        <p className="text-xl my-4">Subtasks</p>

        <SubtaskList subtasks={project.subTasks || []} className="mb-5" />

        {project.closureSummary && (
          <div className="border border-[#EAECF0] rounded-xl p-6 mb-6 w-full">
            <h2 className="font-semibold mb-4">Closure Summary</h2>
            <p className="text-gray-700">{project.closureSummary}</p>
          </div>
        )}

        {project.closureImageUrls?.length > 0 && (
          <div className="border border-[#EAECF0] rounded-xl p-6 mb-6 w-full">
            <h2 className="font-semibold mb-4">
              Closure Images ({project.closureImageUrls.length})
            </h2>
            <div className="grid grid-cols-10 gap-2">
              {project.closureImageUrls.map((url: string, idx: number) => (
                <Zoom key={idx}>
                  <img
                    src={url}
                    alt={`Closure ${idx + 1}`}
                    className="w-[4rem] h-[4rem] object-cover rounded-lg"
                  />
                </Zoom>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinishedProjectPage;
